import React from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  X,
  Gauge,
  Volume2,
  Sparkles,
  AlertCircle,
  Sunrise,
  Sun,
  Moon,
  Palmtree,
  Coffee,
} from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

// --- Lifestyle Section ---
export const LifestyleSection: React.FC<{ data: LivingInAreaData['lifestyleOverview'] }> = ({
  data,
}) => {
  return (
    <section className="w-full max-w-5xl mx-auto py-16 px-6 bg-white">
      <div className="text-center space-y-4 mb-12">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-600 text-white text-xs font-black uppercase tracking-[0.15em]">
          <Sparkles size={14} /> Area Insights
        </span>
        <h2 className="text-4xl font-black text-slate-900 md:text-5xl">
          Lifestyle <span className="text-blue-600">Compatibility</span>
        </h2>
        <p className="text-xl font-bold text-slate-700 max-w-2xl mx-auto">
          Discover if the rhythm of this neighborhood matches your daily flow.
        </p>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <div className="rounded-[2.5rem] border-2 border-slate-200 bg-slate-50 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-600 text-white shadow-md">
              <Check size={24} strokeWidth={3} />
            </div>
            <h3 className="text-2xl font-black text-slate-900">Perfect For</h3>
          </div>
          <ul className="space-y-5">
            {data.bestFor.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-slate-800 font-bold text-lg leading-snug"
              >
                <Check className="mt-1 h-5 w-5 shrink-0 text-green-600" strokeWidth={3} />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-[2.5rem] border-2 border-slate-200 bg-slate-50 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-600 text-white shadow-md">
              <AlertCircle size={24} strokeWidth={3} />
            </div>
            <h3 className="text-2xl font-black text-slate-900">Trade-offs</h3>
          </div>
          <ul className="space-y-5">
            {data.notIdealFor.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-slate-800 font-bold text-lg leading-snug"
              >
                <X className="mt-1 h-5 w-5 shrink-0 text-orange-600" strokeWidth={3} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 mt-8">
        <MetricCard
          icon={<Gauge />}
          label="Pace of Life"
          value={data.paceOfLife}
          color="text-blue-700"
          bg="bg-blue-100"
        />
        <MetricCard
          icon={<Volume2 />}
          label="Noise Levels"
          value={data.noiseLevels}
          color="text-purple-700"
          bg="bg-purple-100"
        />
      </div>
    </section>
  );
};

const MetricCard = ({ icon, label, value, color, bg }: any) => (
  <div className="flex items-center gap-6 p-6 rounded-[2rem] border-2 border-slate-200 bg-white">
    <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${bg} ${color}`}>
      {React.cloneElement(icon, { size: 28, strokeWidth: 2.5 })}
    </div>
    <div>
      <p className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-2xl font-black text-slate-900">{value}</p>
    </div>
  </div>
);

// --- Day in the Life Section ---
interface DayInLifeSectionProps {
  data: LivingInAreaData['dayInTheLife']; // Correctly typed to the sub-property
  areaName: string;
}

export const DayInLifeSection: React.FC<DayInLifeSectionProps> = ({ data, areaName }) => {
  const timeSlots = [
    {
      title: 'Morning',
      text: data.morning,
      time: '6:00 AM - 11:00 AM',
      color: 'text-amber-800',
      bg: 'bg-amber-100',
      icon: <Sunrise />,
    },
    {
      title: 'Daytime',
      text: data.daytime,
      time: '11:00 AM - 5:00 PM',
      color: 'text-blue-800',
      bg: 'bg-blue-100',
      icon: <Sun />,
    },
    {
      title: 'Evening',
      text: data.evening,
      time: '5:00 PM - onwards',
      color: 'text-indigo-800',
      bg: 'bg-indigo-100',
      icon: <Moon />,
    },
  ];

  return (
    <section className="max-w-6xl mx-auto px-6 py-20 space-y-16">
      <div className="text-center space-y-4">
        <h2 className="text-5xl font-black text-slate-900 tracking-tight">
          A Day in <span className="text-teal-600">{areaName}</span>
        </h2>
        <p className="text-xl font-bold text-slate-600">
          Experience the daily rhythm of the community.
        </p>
      </div>

      <div className="grid gap-8 md:grid-cols-3">
        {timeSlots.map((slot, index) => (
          <motion.div
            key={slot.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="relative rounded-[2.5rem] border-2 border-slate-100 bg-white p-8 shadow-xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div
                className={`h-14 w-14 flex items-center justify-center rounded-2xl ${slot.bg} ${slot.color}`}
              >
                {React.cloneElement(slot.icon as React.ReactElement, {
                  size: 28,
                  strokeWidth: 2.5,
                })}
              </div>
              <div>
                <p className={`text-sm font-black uppercase tracking-widest ${slot.color}`}>
                  {slot.title}
                </p>
                <p className="text-xs font-black text-slate-500">{slot.time}</p>
              </div>
            </div>
            <p className="text-lg font-bold leading-relaxed text-slate-800">{slot.text}</p>
          </motion.div>
        ))}
      </div>

      {data.weekendFeel && (
        <div className="relative overflow-hidden rounded-[3rem] bg-slate-900 p-10 md:p-16 text-white shadow-2xl">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 opacity-10">
            <Palmtree size={320} className="text-teal-400" />
          </div>
          <div className="relative z-10 space-y-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500 rounded-lg">
                <Coffee size={24} className="text-slate-900" />
              </div>
              <span className="text-sm font-black uppercase tracking-[0.3em] text-teal-400">
                The Weekend Vibe
              </span>
            </div>
            <p className="text-3xl md:text-5xl font-black leading-tight text-slate-50 italic">
              "{data.weekendFeel}"
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
